/**
 * Хуки для оптимистичных мутаций
 * Мгновенное обновление UI с откатом при ошибке
 */

import { useCallback } from 'react';
import { useSWRConfig } from 'swr';
import { API_URL } from '@/lib/api';
import { getAccessToken } from '@/lib/authStorage';
import {
  createOptimisticOptions,
  optimisticIncrement,
  optimisticPrepend,
  optimisticRemove,
  optimisticUpdate,
} from '../optimistic';

// Хелпер для API запросов
async function apiRequest<T>(
  url: string,
  method: string,
  body?: unknown
): Promise<T> {
  const token = getAccessToken();
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_URL}${url}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const error = new Error(`HTTP ${res.status}`);
    error.name = 'FetchError';
    throw error;
  }

  if (res.status === 204) return null as T;
  return res.json();
}

/**
 * Хук для оптимистичного лайка новости
 */
export function useNewsLike() {
  const { mutate } = useSWRConfig();

  const toggleLike = useCallback(
    async (slug: string, currentLikesCount: number, isLiked: boolean) => {
      const safeSlug = encodeURIComponent(String(slug || '').trim());
      const newLikesCount = isLiked ? currentLikesCount - 1 : currentLikesCount + 1;
      const newIsLiked = !isLiked;

      // Ключ для детальной страницы новости
      const detailKey = `/api/news/${safeSlug}/`;

      // Оптимистичное обновление
      await mutate(
        detailKey,
        async (current: any) => {
          // Делаем запрос на сервер
          await apiRequest(`/api/news/${safeSlug}/like/`, 'POST');
          // Возвращаем обновлённые данные
          return current
            ? { ...current, likes_count: newLikesCount, user_reaction: newIsLiked ? 'like' : null }
            : current;
        },
        createOptimisticOptions(
          (current: any) =>
            current
              ? { ...current, likes_count: newLikesCount, user_reaction: newIsLiked ? 'like' : null }
              : current,
          false // Не ревалидируем сразу — экономим запросы
        )
      );

      return { likesCount: newLikesCount, isLiked: newIsLiked };
    },
    [mutate]
  );

  return { toggleLike };
}

/**
 * Хук для оптимистичного лайка проекта
 */
export function useProjectLike() {
  const { mutate } = useSWRConfig();

  const toggleLike = useCallback(
    async (projectId: number, currentLikesCount: number, isLiked: boolean) => {
      const newLikesCount = isLiked ? currentLikesCount - 1 : currentLikesCount + 1;
      const newIsLiked = !isLiked;

      const detailKey = `/api/projects/${projectId}/`;

      await mutate(
        detailKey,
        async (current: any) => {
          await apiRequest(`/api/projects/${projectId}/like/`, 'POST');
          return current
            ? { ...current, likes_count: newLikesCount, user_liked: newIsLiked }
            : current;
        },
        createOptimisticOptions(
          (current: any) =>
            current
              ? { ...current, likes_count: newLikesCount, user_liked: newIsLiked }
              : current,
          false
        )
      );

      return { likesCount: newLikesCount, isLiked: newIsLiked };
    },
    [mutate]
  );

  return { toggleLike };
}

/**
 * Интерфейс комментария
 */
interface Comment {
  id: number;
  text: string;
  author: { id: number; username: string; display_name?: string };
  created_at: string;
  [key: string]: unknown;
}

/**
 * Хук для оптимистичного добавления комментария к новости
 */
export function useNewsComments(slug: string) {
  const { mutate } = useSWRConfig();
  const safeSlug = encodeURIComponent(String(slug || '').trim());
  const commentsKey = `/api/news/${safeSlug}/comments/`;

  const addComment = useCallback(
    async (text: string, author: { id: number; username: string; display_name?: string }) => {
      // Временный ID для оптимистичного комментария
      const tempId = -Date.now();
      const tempComment: Comment = {
        id: tempId,
        text,
        author,
        created_at: new Date().toISOString(),
      };

      const result = await mutate(
        commentsKey,
        async (current: Comment[] | undefined) => {
          // Отправляем на сервер
          const response = await apiRequest<Comment>(commentsKey, 'POST', { text });
          // Заменяем временный комментарий на реальный
          if (current) {
            return current.map((c) => (c.id === tempId ? response : c));
          }
          return [response];
        },
        createOptimisticOptions(optimisticPrepend(tempComment), true)
      );

      return result;
    },
    [mutate, commentsKey]
  );

  const deleteComment = useCallback(
    async (commentId: number) => {
      await mutate(
        commentsKey,
        async (current: Comment[] | undefined) => {
          await apiRequest(`/api/news/${safeSlug}/comments/${commentId}/delete/`, 'DELETE');
          return current ? current.filter((c) => c.id !== commentId) : [];
        },
        createOptimisticOptions(optimisticRemove<Comment>(commentId), false)
      );
    },
    [mutate, commentsKey, safeSlug]
  );

  return { addComment, deleteComment };
}

/**
 * Хук для оптимистичного добавления комментария к проекту
 */
export function useProjectComments(projectId: number) {
  const { mutate } = useSWRConfig();
  const commentsKey = `/api/projects/${projectId}/comments/`;

  const addComment = useCallback(
    async (text: string, author: { id: number; username: string; display_name?: string }) => {
      const tempId = -Date.now();
      const tempComment: Comment = {
        id: tempId,
        text,
        author,
        created_at: new Date().toISOString(),
      };

      const result = await mutate(
        commentsKey,
        async (current: Comment[] | undefined) => {
          const response = await apiRequest<Comment>(commentsKey, 'POST', { text });
          if (current) {
            return current.map((c) => (c.id === tempId ? response : c));
          }
          return [response];
        },
        createOptimisticOptions(optimisticPrepend(tempComment), true)
      );

      return result;
    },
    [mutate, commentsKey]
  );

  const deleteComment = useCallback(
    async (commentId: number) => {
      await mutate(
        commentsKey,
        async (current: Comment[] | undefined) => {
          await apiRequest(`/api/projects/${projectId}/comments/${commentId}/`, 'DELETE');
          return current ? current.filter((c) => c.id !== commentId) : [];
        },
        createOptimisticOptions(optimisticRemove<Comment>(commentId), false)
      );
    },
    [mutate, commentsKey, projectId]
  );

  return { addComment, deleteComment };
}

/**
 * Хук для оптимистичных реакций на сообщения в мессенджере
 */
export function useMessageReactions() {
  const { mutate } = useSWRConfig();

  const toggleReaction = useCallback(
    async (
      messageId: number,
      emoji: string,
      hasReaction: boolean,
      messagesKey: string
    ) => {
      interface Message {
        id: number;
        reactions?: Array<{ emoji: string; count: number; users: number[] }>;
        [key: string]: unknown;
      }

      await mutate(
        messagesKey,
        async (current: Message[] | undefined) => {
          // Делаем запрос
          const method = hasReaction ? 'DELETE' : 'POST';
          await apiRequest(`/api/chat/message/${messageId}/reaction/`, method, { emoji });

          if (!current) return current;

          // Обновляем реакции в сообщении
          return current.map((msg) => {
            if (msg.id !== messageId) return msg;

            const reactions = msg.reactions || [];
            const existingIdx = reactions.findIndex((r) => r.emoji === emoji);

            if (hasReaction) {
              // Удаляем реакцию
              if (existingIdx >= 0) {
                const updated = [...reactions];
                if (updated[existingIdx].count <= 1) {
                  updated.splice(existingIdx, 1);
                } else {
                  updated[existingIdx] = {
                    ...updated[existingIdx],
                    count: updated[existingIdx].count - 1,
                  };
                }
                return { ...msg, reactions: updated };
              }
            } else {
              // Добавляем реакцию
              if (existingIdx >= 0) {
                const updated = [...reactions];
                updated[existingIdx] = {
                  ...updated[existingIdx],
                  count: updated[existingIdx].count + 1,
                };
                return { ...msg, reactions: updated };
              } else {
                return {
                  ...msg,
                  reactions: [...reactions, { emoji, count: 1, users: [] }],
                };
              }
            }

            return msg;
          });
        },
        {
          rollbackOnError: true,
          populateCache: true,
          revalidate: false,
        }
      );
    },
    [mutate]
  );

  return { toggleReaction };
}

/**
 * Универсальный хук для оптимистичного обновления любых данных
 */
export function useOptimisticMutate() {
  const { mutate } = useSWRConfig();

  const optimisticMutate = useCallback(
    async <T>(
      key: string,
      fetcher: (current: T | undefined) => Promise<T>,
      optimisticData: T | ((current: T | undefined) => T),
      revalidate = true
    ): Promise<T | undefined> => {
      return mutate(key, fetcher, createOptimisticOptions(optimisticData, revalidate));
    },
    [mutate]
  );

  return { optimisticMutate };
}
